import React, { useState } from "react";

import { Box, Typography, CircularProgress, Switch } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";

import ModernModal from "../ModernModal";
import { pricingPlanService } from "./services";
import { PricingPlan } from "./types";
import PricingPlanCard from "./components/PricingPlanCard";

const ModernToggleSwitch = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  justifyContent: "center",
}));

const ToggleLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ isActive }) => ({
  fontSize: "0.9rem",
  fontWeight: 600,
  color: isActive ? "#1f2937" : "#9ca3af",
  cursor: "pointer",
  userSelect: "none",
  transition: "color 0.2s ease",
}));

const ToggleSwitch = styled(Switch)(() => ({
  width: 48,
  height: 28,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(0px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(20px)",
      "& + .MuiSwitch-track": {
        backgroundColor: "#1976d2",
        opacity: 1,
        border: 0,
      },
      "& .MuiSwitch-thumb:before": {
        content: '""',
        position: "absolute",
        width: "100%",
        height: "100%",
        left: 0,
        top: 0,
        opacity: 1,
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "#fff",
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.417 6.894h1.389v2.083H4.888V8.56zm0 4.167h1.389V15h-1.39v-2.278zM2.083 4.75h1.389v2.083H2.083V4.75zm0 4.167h1.389v2.083H2.083V8.917zm0 4.167h1.389v2.083H2.083V13.084zM13.75 8.56h1.389v2.083H13.75V8.56zM13.75 13.084h1.389V15H13.75v-1.916zM13.75 2.75h1.389v2.083H13.75V2.75z"/></svg>')`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "16px",
      },
    },
    "&:not(.Mui-checked)": {
      color: "#1976d2",
      "& + .MuiSwitch-track": {
        backgroundColor: "#e5e7eb",
        opacity: 1,
        border: 0,
      },
      "& .MuiSwitch-thumb:before": {
        content: '""',
        position: "absolute",
        width: "100%",
        height: "100%",
        left: 0,
        top: 0,
        opacity: 1,
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "#1976d2",
        )}" d="M9.305 1.667V3.75h1.389V3.75h-1.39zm-4.417 6.894h1.389v2.083H4.888V8.56zm0 4.167h1.389V15h-1.39v-2.278zM13.75 8.56h1.389v2.083H13.75V8.56zM13.75 13.084h1.389V15H13.75v-1.916zM13.75 2.75h1.389v2.083H13.75V2.75z"/></svg>')`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "16px",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.2)",
    width: 24,
    height: 24,
    margin: 0,
  },
  "& .MuiSwitch-track": {
    borderRadius: 28 / 2,
    opacity: 1,
    backgroundColor: "#e5e7eb",
    boxSizing: "border-box",
  },
}));

const DiscountBadge = styled(Box)(() => ({
  background: "linear-gradient(135deg, #10b981, #059669)",
  color: "#ffffff",
  padding: "4px 10px",
  borderRadius: "16px",
  fontSize: "0.75rem",
  fontWeight: 700,
  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
  marginLeft: "8px",
}));

const HeaderSection = styled(Box)(() => ({
  textAlign: "center",
  marginBottom: "32px",
}));

interface Props {
  handleOpenPricingPlanDialog: () => void;
  open: boolean;
}

export default function PricingPlaneDialog({
  handleOpenPricingPlanDialog,
  open,
}: Props) {
  const [plan, setPlan] = useState("monthly");

  const { data: pricingPlans, isLoading } = useQuery({
    queryKey: ["getBillingPlans"],
    queryFn: () => pricingPlanService.getPricingPlans(),
  });

  const getPricesByInterval = (plans: PricingPlan[], interval: string) => {
    return plans.map((planItem: PricingPlan) => {
      const priceDetail = planItem.prices.find(
        (price) => price.recurring_interval === interval,
      );

      const isPopular = false;

      return {
        id: planItem.id,
        name: planItem.name,
        price_in_cents: priceDetail
          ? (priceDetail.price_in_cents / 100).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })
          : null,
        description: planItem.description,
        credits_per_month: planItem.credits_per_month,
        recurring_interval: priceDetail?.recurring_interval,
        features: planItem.features,
        is_allowed_to_purchase: priceDetail?.is_allowed_to_purchase,
        is_popular: isPopular,
      };
    });
  };

  const pricingPlansData = getPricesByInterval(pricingPlans || [], plan);

  return (
    <ModernModal
      open={open}
      onClose={handleOpenPricingPlanDialog}
      title=""
      maxWidth="xl"
    >
      <Box
        sx={{
          maxWidth: 1200,
          margin: "0 auto",
          paddingBottom: 12,
        }}
      >
        <HeaderSection>
          <Typography
            variant="h5"
            sx={{ textAlign: "center", fontWeight: 500, pb: 6 }}
          >
            Upgrade Your Plan
          </Typography>
          {/* Modern Toggle Switch for Billing Cycle */}
          <Box display="flex" justifyContent="center" mb={2}>
            <ModernToggleSwitch>
              <ToggleLabel
                isActive={plan === "monthly"}
                onClick={() => setPlan("monthly")}
              >
                Monthly
              </ToggleLabel>

              <ToggleSwitch
                checked={plan === "yearly"}
                onChange={(e) =>
                  setPlan(e.target.checked ? "yearly" : "monthly")
                }
                color="primary"
              />

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ToggleLabel
                  isActive={plan === "yearly"}
                  onClick={() => setPlan("yearly")}
                >
                  Annual
                </ToggleLabel>
                <DiscountBadge>Save 30%</DiscountBadge>
              </Box>
            </ModernToggleSwitch>
          </Box>
        </HeaderSection>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={40} sx={{ color: "#1976d2" }} />
          </Box>
        ) : (
          <PricingPlanCard pricingPlansData={pricingPlansData} />
        )}
      </Box>
    </ModernModal>
  );
}
